import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, useAcao } from '../comum';

/**
 * A lista de scripts que este produto pode disparar.
 *
 * 🔴 **Esta tela é a fronteira entre "executar script homologado" e "executar
 * qualquer coisa".** O KACE do cliente tem dezenas de scripts habilitados e em
 * produção: o que reinstala o agente, o que reinicia a máquina, o que alguém
 * escreveu ontem para um teste. Nada disso é autorização para nós.
 *
 * Homologar é um ato humano, com nome, data e justificativa escrita, sobre um
 * script específico. A lista começa vazia em toda instalação e não cresce
 * sozinha — nem por sugestão de IA, nem por sincronização.
 */

interface Homologado {
  readonly id: string;
  readonly scriptIdNoKace: number;
  readonly nome: string;
  readonly descricao: string | null;
  readonly categoria: string | null;
  readonly homologadoPor: string;
  readonly homologadoEm: string;
  readonly justificativa: string;
  readonly ativo: boolean;
  readonly revogadoPor: string | null;
  readonly revogadoEm: string | null;
  readonly motivoRevogacao: string | null;
}

interface Candidato {
  readonly idNoKace: number;
  readonly nome: string;
  readonly descricao: string | null;
  readonly categoria: string | null;
  readonly homologado: boolean;
}

const MINIMO_JUSTIFICATIVA = 15;

export function ScriptsHomologados({
  aoPerderSessao,
  podeHomologar,
}: {
  aoPerderSessao: () => void;
  podeHomologar: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [itens, setItens] = useState<readonly Homologado[] | null>(null);
  const [candidatos, setCandidatos] = useState<readonly Candidato[] | null>(null);
  const [erroKace, setErroKace] = useState<string | null>(null);
  const [escolhido, setEscolhido] = useState<number | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [motivo, setMotivo] = useState<Record<number, string>>({});

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const r = await api.get<{ itens: readonly Homologado[] }>(
        '/api/actions/scripts/homologados',
      );
      setItens(r.itens);
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
      else setItens([]);
    }
    try {
      const c = await api.get<{ itens: readonly Candidato[] }>('/api/actions/scripts/candidatos');
      setCandidatos(c.itens);
      setErroKace(null);
    } catch (e: unknown) {
      // Não conseguir listar candidatos não pode esconder o que já está
      // homologado: revogar precisa funcionar mesmo com o KACE fora do ar.
      setCandidatos([]);
      setErroKace(
        'Não foi possível listar os scripts do KACE. A lista abaixo continua válida; homologar novos exige a conexão de volta.',
      );
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const homologar = (): void => {
    if (escolhido === null) {
      acao.falhar('Escolha o script.');
      return;
    }
    if (justificativa.trim().length < MINIMO_JUSTIFICATIVA) {
      acao.falhar(
        'Descreva o que o script faz e por que este produto pode dispará-lo. ' +
          'É esse texto que a auditoria lê daqui a um ano.',
      );
      return;
    }
    void acao.rodar(async () => {
      await api.post('/api/actions/scripts/homologados', {
        scriptId: escolhido,
        justificativa: justificativa.trim(),
      });
      setEscolhido(null);
      setJustificativa('');
      await carregar();
      return 'Script homologado.';
    });
  };

  const revogar = (h: Homologado): void => {
    const texto = (motivo[h.scriptIdNoKace] ?? '').trim();
    if (texto === '') {
      acao.falhar('Revogar exige motivo — é ele que explica por que o script saiu da lista.');
      return;
    }
    void acao.rodar(async () => {
      await api.post(
        `/api/actions/scripts/homologados/${h.scriptIdNoKace}/revogar`,
        { motivo: texto },
      );
      await carregar();
      return 'Homologação revogada. O script não pode mais ser disparado.';
    });
  };

  const ativos = (itens ?? []).filter((h) => h.ativo);
  const revogados = (itens ?? []).filter((h) => !h.ativo);
  const disponiveis = (candidatos ?? []).filter((c) => !c.homologado);

  return (
    <Painel
      titulo={ativos.length === 0 ? 'Scripts homologados' : `Scripts homologados — ${ativos.length}`}
      sub="Só o que estiver nesta lista pode ser executado pelo produto."
    >
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}
      {erroKace !== null && <Aviso tipo="alerta">{erroKace}</Aviso>}

      <Aviso tipo="info">
        <strong>Estar habilitado no KACE não é homologação.</strong> O appliance do
        cliente tem scripts que reinstalam agente, reiniciam máquina e testes que
        alguém escreveu ontem — todos ligados e em produção. Homologar aqui é dizer
        que <em>este produto</em> pode disparar aquele script específico, com nome e
        motivo registrados. A lista começa vazia e nunca cresce sozinha.
      </Aviso>

      {ativos.length === 0 ? (
        <p className="suave">
          Nenhum script homologado. Nenhuma execução de script é possível até que
          alguém homologue.
        </p>
      ) : (
        ativos.map((h) => (
          <div
            key={h.id}
            style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 14, marginBottom: 14 }}
          >
            <div className="linha">
              <div>
                <strong>{h.nome}</strong>{' '}
                <span className="suave">
                  · script {h.scriptIdNoKace}
                  {h.categoria !== null && ` · ${h.categoria}`}
                </span>
              </div>
              <Etiqueta tom="ok">Homologado</Etiqueta>
            </div>
            {h.descricao !== null && <p className="suave">{h.descricao}</p>}
            <p className="suave">
              Por {h.homologadoPor} em {data(h.homologadoEm)} — {h.justificativa}
            </p>
            {podeHomologar && (
              <div className="linha">
                <input
                  placeholder="Motivo da revogação (obrigatório)"
                  value={motivo[h.scriptIdNoKace] ?? ''}
                  onChange={(e) =>
                    setMotivo({ ...motivo, [h.scriptIdNoKace]: e.target.value })
                  }
                />
                <button
                  className="perigo encolhe"
                  disabled={acao.executando}
                  onClick={() => revogar(h)}
                >
                  Revogar
                </button>
              </div>
            )}
          </div>
        ))
      )}

      {podeHomologar && (
        <>
          <h4 style={{ marginTop: 24 }}>Homologar um script</h4>
          {disponiveis.length === 0 ? (
            <p className="suave">
              {candidatos === null
                ? 'Carregando scripts do KACE…'
                : 'Nenhum script do KACE disponível para homologar. O produto só oferece o que não é de fábrica, está habilitado e está em produção.'}
            </p>
          ) : (
            <>
              <select
                value={escolhido ?? ''}
                onChange={(e) => setEscolhido(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">Escolha o script…</option>
                {disponiveis.map((c) => (
                  <option key={c.idNoKace} value={c.idNoKace}>
                    {c.nome} (#{c.idNoKace})
                  </option>
                ))}
              </select>
              <textarea
                style={{ width: '100%', marginTop: 10 }}
                rows={3}
                placeholder="O que este script faz e por que o produto pode dispará-lo? (obrigatório)"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
              />
              <button
                className="primario encolhe"
                disabled={acao.executando}
                onClick={homologar}
              >
                Homologar
              </button>
            </>
          )}
        </>
      )}

      {revogados.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary className="suave">
            {revogados.length} homologação(ões) revogada(s)
          </summary>
          {/*
            Revogado continua na tela de propósito. Quem auditar uma execução
            antiga precisa achar a homologação que valia no dia — apagar a linha
            deixaria a ação sem explicação.
          */}
          {revogados.map((h) => (
            <p key={h.id} className="suave">
              <strong>{h.nome}</strong> (#{h.scriptIdNoKace}) — revogado por{' '}
              {h.revogadoPor ?? '—'} em {data(h.revogadoEm)}: {h.motivoRevogacao ?? '—'}
            </p>
          ))}
        </details>
      )}
    </Painel>
  );
}
